// Dispatch + counter-sales API — used by TWO logins:
//   dispatchApi / counterApi        → superadmin token   (axiosInstance)
//   opsDispatchApi / opsCounterApi  → ops-admin token    (expensesAxiosInstance)
// The backend accepts both (ops-admin gate = expensadmin OR superuser).

import axiosInstance from './axiosInstance';
import expensesAxiosInstance from './expensesAxiosInstance';
import { API_ENDPOINTS } from '../config/api.config';

const { DISPATCH, COUNTER, OPS, ORDERS, PAYMENTS, CARS, EXCHANGE_PARTS } = API_ENDPOINTS;

const makeDispatchApi = (instance) => ({
  /** ?status=ready|awaiting_payment|dispatched  ·  ?q= */
  list: ({ status = 'ready', q = '' } = {}) =>
    instance.get(DISPATCH.LIST, { params: { status, q: q || undefined } }),
  /** { dispatched: true|false, note } */
  set: (inquiryId, data) => instance.patch(DISPATCH.SET(inquiryId), data),
  /** Day at a glance — dispatched today, ready/awaiting queue, cash book */
  day: () => instance.get(OPS.DAY),
  /** One-tap: confirm the reported payment + dispatch the shipped parts */
  approve: (inquiryId, data = {}) => instance.post(ORDERS.APPROVE(inquiryId), data),
  /** Record & confirm a payment — { payment_mode, payment_type: full|advance, advance_amount? } */
  setPayment: (inquiryId, data) => instance.patch(PAYMENTS.SET(inquiryId), data),
  /** Collect the open advance balance — { paid: true, mode } */
  collectBalance: (inquiryId, data) => instance.patch(PAYMENTS.BALANCE(inquiryId), data),
  /** Won deals + payment state — ?status=needs_action|pending|to_confirm|recorded|balance_due */
  payments: ({ status = 'to_confirm', q = '' } = {}) =>
    instance.get(PAYMENTS.LIST, { params: { status, q: q || undefined } }),
});

const makeCounterApi = (instance) => ({
  /** ?type=walkin|personal · ?date= / ?from=&to= · ?q= */
  list: (params = {}) => instance.get(COUNTER.SALES, { params }),
  /** Same body as walk-in entry POST + sale_type ('personal' default) */
  create: (data) => instance.post(COUNTER.SALES, data),
  /** Fix a wrongly entered sale — partial body, items replace the list */
  update: (id, data) => instance.patch(COUNTER.SALE(id), data),
  /** Remove a wrong entry entirely */
  remove: (id) => instance.delete(COUNTER.SALE(id)),
  /** Collect the open balance of an advance counter sale — { paid, mode? } */
  saleBalance: (id, data) => instance.patch(COUNTER.SALE_BALANCE(id), data),
  /** Payments checkpoint: approve the sale's payment (or send it back) */
  confirmPayment: (id, confirmed = true) =>
    instance.patch(COUNTER.SALE_CONFIRM(id), { confirmed }),
  analytics: () => instance.get(COUNTER.ANALYTICS),
  /** Day at a glance — salesperson sales today + supervisor entry queue */
  day: () => instance.get(OPS.DAY),
  /** Flip a supervisor sale reminder once it's entered in the book */
  reminderEntered: (id) => instance.patch(OPS.SALE_REMINDER(id), { status: 'entered' }),
  /** Phone → existing-customer prefill (name/location + purchase history) */
  checkCustomer: (phone) => instance.get(COUNTER.CHECK_CUSTOMER, { params: { phone_number: phone } }),
  /** Brands→models + all part names, for the form's pickers */
  catalog: () => instance.get(CARS.CATALOG),
  /** Which part types are exchange-eligible (read-only, superadmin-managed) */
  exchangeParts: () => instance.get(EXCHANGE_PARTS.LIST),
  /** Exchange line: tick the old part off when the customer brings it */
  itemExchangeReceived: (itemId, received) =>
    instance.patch(COUNTER.ITEM_EXCHANGE(itemId), { received }),
});

export const dispatchApi = makeDispatchApi(axiosInstance);
export const counterApi = makeCounterApi(axiosInstance);
export const opsDispatchApi = makeDispatchApi(expensesAxiosInstance);
export const opsCounterApi = makeCounterApi(expensesAxiosInstance);
